/**
 * Dispatch forms: stock leaving one of our plants.
 *
 * Only the plant-to-plant transfer exists so far (seedtrac "Transaction – Dispatch Stock Transfer –
 * Plant", manual p453–455 and the Boriya screenshots of 2026-09-15). Its receipt at the other plant is
 * `arrival_stock_transfer`, still on hold.
 */
import { fullTransport } from "./arrival";
import type { FormConfig } from "./types";

/**
 * Dispatch Stock Transfer – Plant: whole lots sent from this plant to another of ours.
 *
 * - The operator names the receiving plant, the carriage, then picks lots by Crop → Variety → Stage
 *   from the ones holding stock here. Each lot goes WHOLE: its row shows the lot's full NoB and Qty
 *   at this plant and nothing is typed (the screen has no quantity box).
 * - Final Submit draws those balances out of their bins (or unbinned stock) and posts them to
 *   `in_transit`, where Inventory shows them as Floating until the receipt draws them in.
 * - The lot keeps its number. A transfer points at the same `lots` row, mints nothing and never
 *   touches `origin_plant_code` (user, 2026-09-16).
 * - Any lot with stock may go, whatever its QC status: seedtrac moves under-testing lots too (user,
 *   2026-09-16, replacing the earlier eligible-only rule).
 * - One number, `STDN/0001/Y/D`, minted when the draft is created and printed on the note.
 *
 * This form has its own page and API module; the config exists for the registry (per-form grants,
 * titles, the number prefix), not for the generic arrival renderer.
 */
export const dispatchStockTransfer: FormConfig = {
  code: "dispatch_stock_transfer",
  docType: "dispatch",
  title: "Dispatch Stock Transfer – Plant",
  numberPrefix: "STDN",
  numberPad: 4,
  sourceKind: "storage",
  destKind: "storage",
  declaredLabel: null,
  declaredFeed: false,
  lotSource: "manual",
  allowManualLots: false,
  blindCoded: false,
  multipleMaterials: true,
  qaRequired: false,
  receivedCaptured: false,
  bagWise: false,
  enforceUniqueLotNumber: false,
  transport: fullTransport,
  dcInfo: false,
  // The Address row beneath is read from the chosen plant's master, never typed or stored.
  headerFields: ["dest_plant"],
  requiredHeaderFields: ["dest_plant"],
  headerLabels: { dest_plant: "Plant Name" },
  lineColumns: [
    "crop",
    "variety",
    "lot_number",
    "stage",
    "declared_bags",
    "declared_qty",
    "actions",
  ],
  documents: ["stdn"],
};

/**
 * Orders and Dispatch (docs/designs/dispatch-module.md, user 2026-10-30): stock leaving for a party.
 * Each has its own page and API module (`modules/sales-dispatch`); the configs are for the registry
 * (per-form grants, titles, the number prefix), as Stock Transfer's is. The party is a Customer
 * Master row (`documents.customer_id`); Material Discard types its party instead.
 */
const salesDispatch = (
  code: string,
  title: string,
  numberPrefix: string
): FormConfig => ({
  ...dispatchStockTransfer,
  code,
  title,
  numberPrefix,
  numberPad: 4,
  destKind: "customer",
  headerFields: [],
  requiredHeaderFields: [],
  headerLabels: {},
});

/** Pack Seed Dispatch: master packs scanned onto the truck against a party's approved orders. */
export const dispatchPack = salesDispatch(
  "dispatch_pack",
  "Pack Seed Dispatch",
  "PD"
);
/** Bulk Seed Dispatch: conditioned seed lots by bags and quantity (SeedTrac's BSDN). */
export const dispatchBulk = salesDispatch(
  "dispatch_bulk",
  "Bulk Seed Dispatch",
  "BD"
);
/** TDF Seed Dispatch: trial / demo / free seed against TDF orders (SeedTrac's TDFSDN). */
export const dispatchTdf = salesDispatch(
  "dispatch_tdf",
  "TDF Seed Dispatch",
  "TD"
);
/** Material Discard: lots sent out to be discarded on a discard instruction (SeedTrac's MDN). */
export const dispatchDiscard = salesDispatch(
  "dispatch_discard",
  "Material Discard",
  "MD"
);
